import React, { Dispatch, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { noop } from 'lodash';
import { useScreenBreakpoint } from 'consts/breakpoints';
import Modal from 'ds/components/modal';
import { RootAction, State } from 'store/state';
import { Route } from 'config/routes';
import { investorsLoginModalOpenSelector, investorsLoginStepSelector } from 'store/state/app/selectors';
import { routeNameSelector } from 'store/state/selectors/router';
import { setInvestorsLoginModalOpen, setInvestorsLoginStep } from 'store/state/app/actions';
import { AuthenticationModalType } from 'components/authentication/types';
import { LoginPage } from 'screens/Investors/Login/LoginPage';
import { ANIMATION_DURATION_MS, SlideUpContent } from './styled';

interface InvestorsLoginModalProps {
  isOpen: boolean;
  // dismissable on the public routes (Home / Neighbourhood-Compare), where visitor-like users
  // browse real content behind the modal; non-dismissable on every other investor page, where the
  // modal is the gate and there's nothing useful behind it — the user must log in (or accept the
  // investors terms) to continue.
  isDismissable: boolean;
  step: AuthenticationModalType;
  onClose: () => void;
}

// full-viewport override — the shared Modal's own ContentWrapper caps width/adds padding+radius,
// none of which apply here since LoginPage is a full-page step-machine, not modal-sized content.
const FULL_SCREEN_CONTENT_STYLE: React.CSSProperties = {
  width: '100vw',
  height: '100vh',
  maxWidth: '100vw',
  margin: 0,
  padding: 0,
  borderRadius: 0,
  overflow: 'hidden',
  background: 'transparent',
};

// pins the close X inside the viewport (the shared Modal's default position sits just outside its
// content box via a negative offset, which only makes sense for a padded, inset modal card).
// z-index is required: LoginPage's own `Content` (screens/Investors/Login/styled.ts) is also
// `position: relative` and sits later in the DOM, so under the default z-index:auto stacking order
// it paints (and hit-tests) above the close button despite rendering visually underneath it.
const CLOSE_BUTTON_STYLE: React.CSSProperties = {
  top: '24px',
  right: '24px',
  zIndex: 10,
};

// On mobile the Welcome step has a full-bleed background image, so the bare dark X blends into it and
// is hard to see. Wrap it in a solid white circle for contrast. Desktop keeps the plain X (the shared
// Modal's CloseButton is flex-centered, so the 24px icon sits centered inside the circle).
const MOBILE_CLOSE_BUTTON_STYLE: React.CSSProperties = {
  ...CLOSE_BUTTON_STYLE,
  top: '34px',
  right: '16px',
  width: '36px',
  height: '36px',
  borderRadius: '50%',
  background: '#fff',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
};

// Renders the investors login step-machine (LoginPage) inside a full-screen modal, so it can be
// triggered from any investors page without navigating away from the current page.
export const InvestorsLoginModalBase: React.FC<InvestorsLoginModalProps> = ({ isOpen, isDismissable, step, onClose }) => {
  const screen = useScreenBreakpoint();
  const isMobile = screen < 3;
  // `shouldRender` controls whether the modal is mounted at all — it stays true for the duration of
  // the slide-down transition after `isOpen` flips to false, so the exit animation can actually play
  // (unmounting immediately would cut it short and drop the shared Modal's scroll-locking
  // OverlayWrapper before the content has visually left the screen).
  //
  // `isVisible` is what actually drives the slide transform, and is intentionally kept one tick
  // behind `shouldRender` on open: a plain CSS `transition` only animates a property that *changes*
  // on an element already in the DOM — inserting a brand-new element whose transform is already
  // correct (isOpen right from the first render) gives the browser no "before" frame to animate
  // from, so the enter animation never played. Mounting first with isVisible=false, then flipping
  // to true a couple of frames later (double rAF, to guarantee the closed frame is actually
  // painted first) gives it that "before" state.
  const [ shouldRender, setShouldRender ] = useState(isOpen);
  const [ isVisible, setIsVisible ] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      let innerRafId: number;
      const outerRafId = requestAnimationFrame(() => {
        innerRafId = requestAnimationFrame(() => setIsVisible(true));
      });
      return () => {
        cancelAnimationFrame(outerRafId);
        if (innerRafId) cancelAnimationFrame(innerRafId);
      };
    }

    setIsVisible(false);
    const timeout = setTimeout(() => setShouldRender(false), ANIMATION_DURATION_MS);
    return () => clearTimeout(timeout);
  }, [ isOpen ]);

  if (!shouldRender) return null;

  // On non-dismissable routes the modal is the gate: no close affordance, and backdrop-click / esc
  // (which both route through onClose) are disabled by handing the Modal a no-op.
  const handleClose = isDismissable ? onClose : noop;

  // LoginPage itself only renders a Back arrow once a step is selected (i.e. past Welcome) — the
  // modal's own close X fills that gap on the first (Welcome) step only, so there's always exactly
  // one dismiss affordance (when the modal is dismissable at all).
  const showCloseButton = isDismissable && step === null;

  return (
    <Modal
      isOpen
      desktopModalPositionCenter
      isFullWidthMainContent
      onClose={handleClose}
      hideCloseButton={!showCloseButton}
      disableBackdropClick={!isDismissable}
      closeButtonHorizontalPosition="right"
      closeButtonStyle={isMobile ? MOBILE_CLOSE_BUTTON_STYLE : CLOSE_BUTTON_STYLE}
      contentWrapperStyle={FULL_SCREEN_CONTENT_STYLE}
    >
      <SlideUpContent isOpen={isVisible}>
        <LoginPage />
      </SlideUpContent>
    </Modal>
  );
};

const mapStateToProps = (state: State) => {
  const routeName = routeNameSelector(state);
  return {
    isOpen: investorsLoginModalOpenSelector(state),
    isDismissable: routeName === Route.InvestorsHomePage || routeName === Route.InvestorsNeighbourhoodPage,
    step: investorsLoginStepSelector(state),
  };
};

const mapDispatchToProps = (dispatch: Dispatch<RootAction>) => ({
  onClose: () => {
    dispatch(setInvestorsLoginModalOpen(false));
    dispatch(setInvestorsLoginStep({ type: null }, { isUserInitiated: true }));
  },
});

export const InvestorsLoginModal = connect(mapStateToProps, mapDispatchToProps)(InvestorsLoginModalBase);
